var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};

// src/index.ts
import { z as z9 } from "zod";

// src/builder.ts
import { z as z5 } from "zod";

// src/grab.ts
import { z as z2 } from "zod";

// src/selectionUtils.ts
import { z } from "zod";

// src/baseQuery.ts
var BaseQuery = class {
  constructor({ query, schema }) {
    this.query = query;
    this.schema = schema;
  }
  value() {
    return { schema: this.schema, query: this.query };
  }
  nullable() {
    return new NullableBaseQuery({
      query: this.query,
      schema: this.schema.nullable()
    });
  }
};
var NullableBaseQuery = class extends BaseQuery {
  constructor({ schema, query }) {
    super({ schema: schema.nullable(), query });
  }
};

// src/typeGuards.ts
function extendsBaseQuery(v) {
  return v instanceof BaseQuery;
}
function isQuerySchemaTuple(v) {
  return Array.isArray(v);
}

// src/selectionUtils.ts
function getProjectionEntriesFromSelection(selection) {
  return Object.entries(selection).reduce((acc, [key, val]) => {
    let toPush = "";
    if (extendsBaseQuery(val)) {
      toPush = `"${key}": ${val.query}`;
    } else if (isQuerySchemaTuple(val)) {
      toPush = `"${key}": ${val[0]}`;
    } else {
      toPush = key;
    }
    if (toPush)
      acc.push(toPush);
    return acc;
  }, []);
}
function getSchemaFromSelection(selection) {
  return z.object(
    Object.entries(selection).reduce((acc, [key, value]) => {
      if (extendsBaseQuery(value)) {
        acc[key] = value.schema;
      } else if (isQuerySchemaTuple(value)) {
        acc[key] = value[1];
      } else if (value instanceof z.ZodType) {
        acc[key] = value;
      }
      return acc;
    }, {})
  );
}

// src/grab.ts
var grab = (query, schema, selection, conditionalSelections) => {
  const projections = getProjectionEntriesFromSelection(selection);
  if (conditionalSelections) {
    const condProjections = Object.entries(conditionalSelections).reduce((acc, [key, val]) => {
      acc.push(
        `${key} => { ${getProjectionEntriesFromSelection(val).join(", ")} }`
      );
      return acc;
    }, []);
    projections.push(`...select(${condProjections.join(", ")})`);
  }
  const newSchema = (() => {
    const conditionalFields = Object.values(
      conditionalSelections || {}
    );
    const baseSchema = getSchemaFromSelection(selection);
    const conditionalFieldSchemas = conditionalFields.map(
      (field) => baseSchema.merge(getSchemaFromSelection(field))
    );
    const unionEls = [...conditionalFieldSchemas, baseSchema];
    const s = unionEls.length > 1 ? z2.union([unionEls[0], unionEls[1], ...unionEls.slice(2)]) : unionEls[0];
    return schema instanceof z2.ZodArray ? z2.array(s) : s;
  })();
  const res = newSchema instanceof z2.ZodArray ? new ArrayQuery({
    query: query + `{${projections.join(", ")}}`,
    schema: newSchema
  }) : new EntityQuery({
    query: query + `{${projections.join(", ")}}`,
    schema: newSchema
  });
  return res;
};

// src/select.ts
import { z as z3 } from "zod";
var select = (conditionalSelections) => {
  const getProjection = (v) => {
    if (extendsBaseQuery(v)) {
      return v.query;
    }
    if (isQuerySchemaTuple(v)) {
      return v[0];
    }
    return `{ ${getProjectionEntriesFromSelection(v).join(", ")} }`;
  };
  const _a = conditionalSelections, { default: defaultSelection } = _a, selections = __objRest(_a, ["default"]);
  const condProjections = Object.entries(selections).reduce(
    (acc, [condition, val]) => {
      acc.push(`${condition} => ${getProjection(val)}`);
      return acc;
    },
    []
  );
  if (defaultSelection) {
    condProjections.push(getProjection(defaultSelection));
  }
  const query = `select(${condProjections.join(", ")})`;
  const newSchema = (() => {
    const toSchemaInput = (v) => {
      if (extendsBaseQuery(v))
        return v.schema;
      if (isQuerySchemaTuple(v)) {
        return v[1];
      }
      return getSchemaFromSelection(v);
    };
    const unionEls = Object.values(conditionalSelections).map(toSchemaInput);
    return conditionalSelections.default ? (
      // @ts-expect-error unionEls will be a tuple
      z3.union(unionEls)
    ) : (
      // @ts-expect-error unionEls will be a tuple
      z3.union([z3.null(), ...unionEls])
    );
  })();
  return new BaseQuery({
    query,
    schema: newSchema
  });
};
var emptyZodRecord = z3.record(z3.string(), z3.never());
function spreadUnionSchema(union) {
  const spreadOptions = union.options.map(
    (option) => {
      if ("shape" in option)
        return option;
      if ("options" in option)
        return spreadUnionSchema(option);
      return z3.record(z3.string(), z3.never());
    }
  );
  return z3.union(spreadOptions);
}

// src/nullToUndefined.ts
import { preprocess, z as z4 } from "zod";
var _nullToUndefined = (schema) => {
  return preprocess((arg) => arg === null ? void 0 : arg, schema);
};
function nullToUndefined(schemaOrSelection) {
  if (schemaOrSelection instanceof z4.ZodType)
    return _nullToUndefined(schemaOrSelection);
  return Object.entries(schemaOrSelection).reduce((acc, [key, value]) => {
    if (value instanceof z4.ZodType)
      acc[key] = _nullToUndefined(value);
    else if (Array.isArray(value))
      acc[key] = [value[0], _nullToUndefined(value[1])];
    else
      acc[key] = value;
    return acc;
  }, {});
}
function nullToUndefinedOnConditionalSelection(conditionalSelection) {
  if (!conditionalSelection)
    return conditionalSelection;
  return Object.entries(conditionalSelection).reduce((acc, [key, value]) => {
    acc[key] = nullToUndefined(value);
    return acc;
  }, {});
}

// src/builder.ts
var EntityQuery = class extends BaseQuery {
  constructor(payload) {
    super(payload);
  }
  filter(filterValue = "") {
    this.query += `[${filterValue}]`;
    return new ArrayQuery({
      query: this.query,
      schema: z5.array(this.schema)
    });
  }
  select(s) {
    const _select = extendsBaseQuery(s) ? s : select(s);
    return new EntityQuery({
      query: this.query + `{...${_select.query}}`,
      schema: spreadUnionSchema(_select.schema)
    });
  }
  grab(selection, conditionalSelections) {
    return grab(this.query, this.schema, selection, conditionalSelections);
  }
  grab$(selection, conditionalSelections) {
    return grab(
      this.query,
      this.schema,
      nullToUndefined(selection),
      conditionalSelections ? nullToUndefinedOnConditionalSelection(conditionalSelections) : conditionalSelections
    );
  }
  grabOne(name, fieldSchema) {
    return new EntityQuery({
      query: this.query + `.${name}`,
      schema: fieldSchema || z5.unknown()
    });
  }
  grabOne$(name, fieldSchema) {
    return new EntityQuery({
      query: this.query + `.${name}`,
      schema: nullToUndefined(fieldSchema || z5.unknown())
    });
  }
  deref() {
    return new UnknownQuery(__spreadProps(__spreadValues({}, this.value()), {
      query: this.query + "->"
    }));
  }
};
var UnknownQuery = class extends EntityQuery {
  constructor(payload) {
    super(__spreadProps(__spreadValues({}, payload), { schema: z5.unknown() }));
  }
  // filter to an unknown array
  filter(filterValue = "") {
    this.query += `[${filterValue}]`;
    return new UnknownArrayQuery(__spreadValues({}, this.value()));
  }
  filterByType(filterTypeValue) {
    return this.filter(`_type == '${filterTypeValue}'`);
  }
};
var ArrayQuery = class extends BaseQuery {
  constructor(payload) {
    super(payload);
  }
  select(s) {
    const _select = extendsBaseQuery(s) ? s : select(s);
    return new ArrayQuery({
      query: this.query + `{...${_select.query}}`,
      schema: z5.array(spreadUnionSchema(_select.schema))
    });
  }
  filter(filterValue = "") {
    return new ArrayQuery(__spreadProps(__spreadValues({}, this.value()), {
      query: this.query + `[${filterValue}]`
    }));
  }
  filterByType(filterTypeValue) {
    return this.filter(`_type == '${filterTypeValue}'`);
  }
  grab(selection, conditionalSelections) {
    return grab(this.query, this.schema, selection, conditionalSelections);
  }
  grab$(selection, conditionalSelections) {
    return grab(
      this.query,
      this.schema,
      nullToUndefined(selection),
      conditionalSelections ? nullToUndefinedOnConditionalSelection(conditionalSelections) : conditionalSelections
    );
  }
  grabOne(name, fieldSchema) {
    return new ArrayQuery({
      query: this.query + `.${name}`,
      schema: z5.array(fieldSchema || z5.unknown())
    });
  }
  grabOne$(name, fieldSchema) {
    return new ArrayQuery({
      query: this.query + `.${name}`,
      schema: z5.array(nullToUndefined(fieldSchema || z5.unknown()))
    });
  }
  order(...orderings) {
    return new ArrayQuery(__spreadProps(__spreadValues({}, this.value()), {
      query: this.query + `|order(${orderings.join(", ")})`
    }));
  }
  score(...scores) {
    return new ArrayQuery(__spreadProps(__spreadValues({}, this.value()), {
      query: this.query + `|score(${scores.join(", ")})`
    }));
  }
  slice(min, max) {
    this.query += `[${min}${typeof max === "number" ? `..${max}` : ""}]`;
    if (typeof max === "undefined") {
      return new EntityQuery(__spreadProps(__spreadValues({}, this.value()), {
        schema: this.schema.element
      }));
    }
    return this;
  }
};
var UnknownArrayQuery = class extends ArrayQuery {
  constructor(payload) {
    super(__spreadProps(__spreadValues({}, payload), { schema: z5.array(z5.unknown()) }));
  }
  deref() {
    this.query += "->";
    return this;
  }
};

// src/schemas.ts
import { z as z7 } from "zod";

// src/contentBlock.ts
import { z as z6 } from "zod";
function contentBlock({ markDefs } = {}) {
  return makeContentBlockQuery(markDefs || baseMarkdefsType);
}
function contentBlocks({ markDefs } = {}) {
  return z6.array(makeContentBlockQuery(markDefs || baseMarkdefsType));
}
function makeContentBlockQuery(markDefs) {
  return z6.object({
    _type: z6.string(),
    _key: z6.string().optional(),
    children: z6.array(
      z6.object({
        _key: z6.string(),
        _type: z6.string(),
        text: z6.string(),
        marks: z6.array(z6.string())
      })
    ),
    markDefs: z6.array(markDefs).optional(),
    style: z6.string().optional(),
    listItem: z6.string().optional(),
    level: z6.number().optional()
  });
}
var baseMarkdefsType = z6.object({
  _type: z6.string(),
  _key: z6.string()
}).catchall(z6.unknown());

// src/schemas.ts
var dateSchema = () => z7.preprocess((arg) => {
  if (typeof arg == "string" || arg instanceof Date)
    return new Date(arg);
}, z7.date());
var slug = (fieldName) => [`${fieldName}.current`, z7.string()];
var schemas = {
  string: z7.string,
  number: z7.number,
  boolean: z7.boolean,
  unknown: z7.unknown,
  null: z7.null,
  undefined: z7.undefined,
  date: dateSchema,
  literal: z7.literal,
  union: z7.union,
  array: z7.array,
  object: z7.object,
  slug,
  contentBlock,
  contentBlocks
};

// src/sanityImage.ts
var reffedAssetFields = {
  _ref: schemas.string(),
  _type: schemas.literal("reference")
};
var cropFields = {
  crop: schemas.object({
    top: schemas.number(),
    bottom: schemas.number(),
    left: schemas.number(),
    right: schemas.number()
  }).nullable()
};
var hotspotFields = {
  hotspot: schemas.object({
    x: schemas.number(),
    y: schemas.number(),
    height: schemas.number(),
    width: schemas.number()
  }).nullable()
};
var refBase = {
  _key: schemas.string().nullable(),
  _type: schemas.string()
};
var dereffedAssetBaseFields = {
  _id: schemas.string(),
  _type: schemas.union([
    schemas.literal("sanity.imageAsset"),
    schemas.literal("sanity.fileAsset")
  ]),
  _rev: schemas.string(),
  extension: schemas.string(),
  mimeType: schemas.string(),
  originalFilename: schemas.string(),
  path: schemas.string(),
  sha1hash: schemas.string(),
  size: schemas.number(),
  url: schemas.string(),
  _updatedAt: schemas.string().nullable()
};
var paletteFieldSchema = {
  _type: schemas.literal("sanity.imagePaletteSwatch"),
  background: schemas.string().optional().nullable(),
  foreground: schemas.string().optional().nullable(),
  population: schemas.number().optional().nullable(),
  title: schemas.string().optional().nullable()
};
var dimensionFields = {
  dimensions: schemas.object({
    _type: schemas.literal("sanity.imageDimensions").optional(),
    aspectRatio: schemas.number(),
    height: schemas.number(),
    width: schemas.number()
  }).nullable()
};
var locationFields = {
  location: schemas.object({
    _type: schemas.literal("geopoint"),
    lat: schemas.number(),
    lng: schemas.number()
  }).nullable()
};
var lqipFields = {
  lqip: schemas.string()
};
var isOpaqueFields = {
  isOpaque: schemas.boolean().nullable()
};
var hasAlphaFields = {
  hasAlpha: schemas.boolean().nullable()
};
var blurHashFields = {
  blurHash: schemas.string().nullable()
};
var getPaletteField = () => schemas.object(paletteFieldSchema).nullable();
var paletteFields = {
  palette: schemas.object({
    darkMuted: getPaletteField(),
    darkVibrant: getPaletteField(),
    dominant: getPaletteField(),
    lightMuted: getPaletteField(),
    lightVibrant: getPaletteField(),
    muted: getPaletteField(),
    vibrant: getPaletteField()
  })
};
function sanityImage(fieldName, options) {
  const { withCrop, withHotspot, additionalFields, isList, withAsset } = options || {};
  let asset = new UnknownQuery({ query: "asset" });
  if (withAsset) {
    asset = asset.deref();
  }
  const assetIncludes = (field) => typeof (withAsset == null ? void 0 : withAsset.includes) === "function" ? withAsset.includes(field) : false;
  const metadataFields = Object.assign(
    {},
    assetIncludes("dimensions") && dimensionFields,
    assetIncludes("location") && locationFields,
    assetIncludes("lqip") && lqipFields,
    assetIncludes("palette") && paletteFields,
    assetIncludes("isOpaque") && isOpaqueFields,
    assetIncludes("hasAlpha") && hasAlphaFields,
    assetIncludes("blurHash") && blurHashFields
  );
  const assetFields = Object.assign(
    {},
    !withAsset && reffedAssetFields,
    assetIncludes("base") && dereffedAssetBaseFields,
    Object.keys(metadataFields).length > 0 && {
      metadata: new UnknownQuery({ query: "metadata" }).grab(metadataFields)
    }
  );
  const toGrab = Object.assign(
    {},
    refBase,
    { asset: asset.grab(assetFields) },
    withCrop === true ? cropFields : {},
    withHotspot === true ? hotspotFields : {},
    additionalFields || {}
  );
  return isList === true ? new UnknownQuery({ query: fieldName }).filter().grab(toGrab) : new UnknownQuery({ query: fieldName }).grab(toGrab);
}

// src/addDeprecationMessage.ts
function addDeprecationMessage(fn, message) {
  return function(...args) {
    console.warn(message);
    return fn(...args);
  };
}

// src/makeSafeQueryRunner.ts
import { z as z8 } from "zod";
var makeSafeQueryRunner = (fn) => ({ query, schema }, ...rest) => fn(query, ...rest).then((res) => {
  try {
    return schema.parse(res);
  } catch (e) {
    if (e instanceof z8.ZodError)
      throw new GroqdParseError(e, res);
    throw e;
  }
});
var GroqdParseError = class extends Error {
  // zodError: z.ZodError;
  constructor(err, rawResponse) {
    const errorMessages = err.errors.map(
      (e) => `	\`result${e.path.reduce((acc, el) => {
        if (typeof el === "string") {
          return `${acc}.${el}`;
        }
        return `${acc}[${el}]`;
      }, "")}\`: ${e.message}`
    );
    super(`Error parsing:
${errorMessages.join("\n")}`);
    this.err = err;
    this.zodError = err;
    this.rawResponse = rawResponse;
  }
};

// src/index.ts
function pipe(filter, { isArray = false } = {}) {
  return isArray ? new UnknownArrayQuery({ query: filter }) : new UnknownQuery({ query: filter });
}
pipe.sanityImage = addDeprecationMessage(
  sanityImage,
  "`q.sanityImage` has been deprecated in favor of importing `sanityImage` directly from `groqd`. `q.sanityImage` will be removed in future versions."
);
pipe.select = select;
Object.assign(pipe, schemas);
var q = pipe;
export {
  BaseQuery,
  GroqdParseError,
  makeSafeQueryRunner,
  nullToUndefined,
  pipe,
  q,
  sanityImage,
  z9 as z
};
//# sourceMappingURL=index.mjs.map